import { ReactElement, FormEvent, useState, useCallback, useEffect, useRef } from "react";
import useApiState, { api } from "../../hooks/use-api-state";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import { pointsText } from "../../utils/pluralize";
import styles from "./Assessment.module.css";
import { IAssessmentObject, IAnswersData, IAnswers, IQuestion, IQuestionAnswer } from "../../messages/IAssessments";
import TinyProgressBar from "../common/TinyProgressBar";
import { IVMData } from "../../messages/IGetUserContentResponse";

const allowedQuestionTypes = ["closed", "multiple", "semaphore", "text", "lab"];

let pollingImageId : string | null = null;

let timer: NodeJS.Timer | null = null;

export default function Assessment(props: {
    appInfo: IAppInfo,
    userInfo: IUserInfo,
    anonymous: boolean
    assessmentObject: IAssessmentObject,
    questions: IQuestion[],
    answersData: IAnswersData|null,
    showOnly: boolean,
    allowFilling: boolean,
    allowSubmit: boolean,
    canSave: boolean,
    recaptchaToken: string|null,
    setMessage: (message: string|null) => void,
    setElapsed?: React.Dispatch<React.SetStateAction<number | null>>,
    updateAssessment: (submitting:boolean, assessment_object:IAssessmentObject, answers_data: IAnswersData|null) => void
  }): ReactElement {
    const effectRunCount = useRef(0); // To detect multiple invocations of useEffect in Strict development mode

    const [submitApiState, setSubmitApiState] = useApiState(false);
    const [questions, setQuestions] = useState<IQuestion[] | null>(props.questions);
    const [answersData, setAnswersData] = useState<IAnswersData | null>(props.answersData);
    const [answers, setAnswers] = useState<IAnswers>(props.answersData?.answers ?? {});
    const [elapsedCounter, setElapsedCounter] = useState<boolean>(false);

    // Lab
    const [labStatus, setLabStatus] = useState<string>("");
    const [progressbarPercentage, setProgressbarPercentage] = useState<number|null>(null);

    useEffect(() => {
        effectRunCount.current += 1;

        const handleBeforeUnload = (e:Event) => { // Cleanup code on page unload
            setElapsedCounter(false);
        };

        if (effectRunCount.current===1) { // Only run this effect once - in development (Strict) mode this is invoked twice
            labcontrol_init(props.appInfo.gcs_address, props.userInfo?.user_id, props.appInfo.csrf_token);
            const submitted = !!answersData?.submitted;
            if (submitted) {
                setElapsedCounter(false);
            }
            else {
                calcElapsed();
                setElapsedCounter(true);
            }
            window.addEventListener("beforeunload", handleBeforeUnload);
        }

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            if (pollingImageId) 
                stopVmStatePolling(pollingImageId);
        };
    }, []);


    useEffect(() => {
        if (elapsedCounter) {
            timer = setInterval(calcElapsed, 1000);
        }
        else {
            if (timer) {
                clearInterval(timer);
                timer = null;
            }
        }
    }, [elapsedCounter]);


    function calcElapsed() {
        const now = Math.floor(Date.now() / 1000);
        const elapsed = now - props.assessmentObject.started_at;
        //console.log(`now=${now} started_at=${props.assessmentObject.started_at} ... elapsed: ${elapsed}`);
        if (props.setElapsed)
            props.setElapsed(elapsed);
        const minutes = Math.trunc((elapsed) / 60);
        if (props.assessmentObject.fillout_time && minutes >= props.assessmentObject.fillout_time) {
            window.scrollTo({ top: 0, left: 0});
            props.setMessage("Time is up! You can start a new assessment any time.");
            setElapsedCounter(false);
            if (props.setElapsed)
                props.setElapsed(null);
            props.assessmentObject.active = false; // Not active anymore, but still shown
            props.assessmentObject.shown = true;
            props.updateAssessment(false, props.assessmentObject, null);
        }
        else {
            //console.log(`props.assessmentObject.can_start=${props.assessmentObject.can_start}`);
            if (!props.assessmentObject.active) { // If we are runnning the assessment, it cannot (re)start
                props.assessmentObject.active = true; // Active and shown
                props.assessmentObject.shown = true;
                props.updateAssessment(false, props.assessmentObject, null);
            }
        }
    }


    const continuouslyPollVM = useCallback((vm: IVMData) => {
        pollingImageId = vm.image_id;
        pollVmState(vm.image_id, vm.region, vm.cohort_id,
          (imageId, vm_data) => {
            //setApiState((state: IApiState) => { return { ...state, isLoading: false, }; });
            const progress = vm_data.progress;
            if (progress && progress.status) {
              var status = progress.status ?? "";
              if (progress.proc) {
                setProgressbarPercentage(parseInt(progress.proc ?? "0"));
              } else {
                setProgressbarPercentage(null);
                //if (status==="stopping") // Cheating: stopping w/o percentage means that the VM is being shut down automatically due to inactivity 
                //  status = "auto_stopping";
              }
              setLabStatus(status);
            } else {
              var state = vm_data.state ?? "";
              //if (state==="stopping") // Cheating: stopping in state (and not in progress) means that the VM is being shut down automatically due to inactivity 
              //  state = "auto_stopping";
              setLabStatus(state);
              setProgressbarPercentage(null);
            }
          },
          (message, log) => {
            console.log(`ERROR OCCURRED: ${message}`);
            console.log(`-${log}`);
          }
        );
    }, [setProgressbarPercentage, setLabStatus]);
    

    function getAnswerValue(questionType: string, questionAnswers: any, answerKey: string|null): string|null {
        let answer = null;
        if (questionType === "closed" && typeof questionAnswers === "string") {
            // It's directly a string
            answer = questionAnswers || null;
        }
        if (questionType === "multiple" && typeof questionAnswers === "object" && answerKey !== null) {
            // If has a value (it's "on"), then return they key (which will match the value, making it checked), otherwise null
            answer = questionAnswers[answerKey] ? answerKey : null;
            //console.log(`(${questionType}) answerKey=${answerKey} --> answer=${answer}`);
        }
        else if (questionType === "semaphore" && typeof questionAnswers === "object" && answerKey !== null) {
            // The value is one of (good, partial, wrong)
            answer = questionAnswers[answerKey] || null;
        }
        /*
        if (type === "text") {
            const textArea = document.getElementById(questionId) as HTMLTextAreaElement;
            if (textArea) {
                return textArea.value || null;
            }
        }
        if (type === "lab") {
            // Handle lab question type if needed
            return null;
        }
        */
        //console.log(`questionId=${questionId} (${questionType}) answerKey=${answerKey} --> answer=${answer}`);
        return answer;
    }

    async function closedClicked(event: FormEvent<HTMLInputElement>) {
        const id = event.currentTarget.id;
        const value = event.currentTarget.value;
        const questionId = id.split("/")[0];
        //const answerKey = id.split("/")[1];
        //console.log(`Closed clicked! questionId=<${questionId}> answerKey=<${answerKey}> value=${value}`);
        if (questionId) {
            setAnswers((prevAnswers) => ({
                ...prevAnswers,
                [questionId]: value,
            }));
        }
    }

    async function multipleClicked(event: FormEvent<HTMLInputElement>) {
        const id = event.currentTarget.id;
        const checked = event.currentTarget.checked;
        const questionId = id.split("/")[0];
        const answerKey = id.split("/")[1];
        //console.log(`Multiple clicked! questionId=${questionId} answerKey=${answerKey} checked=${checked}`);
        if (questionId) {
            setAnswers((prevAnswers) => ({
                ...prevAnswers,
                [questionId]: {
                    ...(typeof prevAnswers[questionId] === "object" && prevAnswers[questionId] !== null ? prevAnswers[questionId] : {}),
                    [answerKey]: checked ? "on" : "",
                },
            }));
        }
    }


    async function semaphoreClicked(event: FormEvent<HTMLInputElement>) {
        const id = event.currentTarget.id;
        const value = event.currentTarget.value;
        const questionId = id.split("/")[0];
        const answerKey = id.split("/")[1];
        //console.log(`Semaphore clicked! id=${id} -> questionId=${questionId} answerKey=${answerKey} value=${value}`);
        if (questionId) {
            setAnswers((prevAnswers) => ({
                ...prevAnswers,
                [questionId]: {
                    ...(typeof prevAnswers[questionId] === "object" && prevAnswers[questionId] !== null ? prevAnswers[questionId] : {}),
                    [answerKey]: value,
                },
            }));
        }
    }


    async function saveAssessment(submit: boolean) {
        //console.log(`Answers: ${JSON.stringify(answers)}`);
        try {
            props.setMessage(submit ? "Submitting assessment..." : "Saving assessment...");
            if (submit) {
                setElapsedCounter(false);
                props.updateAssessment(true, props.assessmentObject, props.answersData);
            }
            window.scrollTo({ top: 0, left: 0});
            let ret = await api.submitAssessment(
                setSubmitApiState,
                submit,
                props.assessmentObject.cohort_id,
                props.assessmentObject.lmodule_code,
                props.assessmentObject.questions_id,
                answers,
                props.assessmentObject?.fullname,
                props.assessmentObject?.email,
                props.recaptchaToken,
                );
            // Since shown is not set in the API, we set it here (the assessment is shown if we are here)
            ret.assessment_obj.shown = true;
            const status = ret.status;
            if (props.anonymous) { // Otherwise the message will arrive via SQS
                if (ret.message) {
                    props.setMessage(ret.message);
                }
                else {
                    props.setMessage(null);
                }
            }
            else {
                if (ret.message)
                    props.setMessage(ret.message);
            }
            //console.log(`Assessment save/submit response: ${status} message=${ret.message}`);
            //console.log(`-assessment_obj: ${JSON.stringify(ret.assessment_obj)}`);
            if (status === "ok") {
                setQuestions(ret.questions??null);
                setAnswersData(ret.answers_data??null);
                setAnswers(ret.answers_data?.answers??{});
                //props.setMessage(`Assessment ${submit ? "submitted" : "saved"}.`);
                if (ret.answers_data)
                    props.updateAssessment(false, ret.assessment_obj, ret.answers_data);
                if (!submit) { // Otherwise the message will arrive via SQS
                    props.setMessage("Assessment saved.");
                }
            }
            else {
                if (ret.message) {
                    props.setMessage(ret.message);
                }
                else {
                    props.setMessage("Error submitting assessment. Please try again later.");
                }   
            }
        } catch (e: any) {
            console.error("Error submitting answers:", e);
        }
    }

    /* If it's not enabled OR there are no questions (note that when it's not enabled, questions attribute is removed) */
    if (!answers || !props.assessmentObject || !questions)
        return <></>
    else {
        const enabled = props.assessmentObject.enabled;
        const active = props.assessmentObject.active;
        const submitted = !!answersData?.submitted;
        const disabled = !props.allowFilling || submitted || !enabled;

        //console.log(`allowFilling=${props.allowFilling} allowSubmit=${props.allowSubmit} canSave=${props.canSave} submitted:${submitted} enabled:${enabled} => disabled:${disabled}`);
        //console.log(`props.assessmentObject=${JSON.stringify(props.assessmentObject)}`);
        return (
        <>
            <div className={styles.assessmentHeader}>
                <h1 className={styles.title}>{props.assessmentObject.title}{props.assessmentObject.multiple_lmodules ? ` (${props.assessmentObject.lmodule_title ?? ""})` : ""}</h1>
                {props.anonymous && props.showOnly && (
                    <div className={styles.fullname}>Results for {props.assessmentObject.fullname ?? ""}</div>
                )}
                <Loading isLoading={submitApiState.isLoading}>
                    {submitted && (
                        <div>
                            <div className={styles.scoreText}>
                                {props.allowFilling && !!props.assessmentObject.fullname ? (<i>{props.assessmentObject.fullname}</i>) : (<span>You</span>)} scored&nbsp;&nbsp;<b><span id="score">{answersData.points}</span>/<span id="maxscore">{answersData.max_points}</span></b>&nbsp;&nbsp;points in this assessment (<b><span id="scorepercent">{answersData.passed_percentage}%</span></b>).
                            </div>
                            <div className={styles.scoreText}>
                                {props.assessmentObject.qualification && !answersData.passed && (
                                    <span>You did not reach the qualification threshold of {props.assessmentObject.threshold_percentage}%. {answersData.can_restart_at_text && (<>You can try again after {answersData.can_restart_at_text}.</>)}</span>
                                )}
                            </div>
                        </div>
                    )}
                </Loading>
                {submitted && answersData.passed && props.assessmentObject.qualified && (
                <div className={styles.certificateContainer}>
                    <a href={props.assessmentObject.qualification_certificate_link} target="_blank" rel="noopener noreferrer">
                        <img src="/static/img/cydrill_qualified_badge.png" alt="Cydrill Qualified" style={{ width:50, height:50}}/>
                    </a>
                    <a className={styles.certificateText} href={props.assessmentObject['qualification_certificate_link']} target="_blank" rel="noopener noreferrer" style={{fontSize:14}}>
                        <center>Qualified!</center>
                    </a>
                </div>
                )}
            </div>
            <form className={styles.assessmentForm}>
            {questions.map((question, index) => {
                let result = props.answersData?.results?.[question.id] ?? {};
                const score = Number(result["score"]) ?? 0;
                if (!allowedQuestionTypes.includes(question.type)) {
                    return (
                        <></>
                    );
                }
                else {
                    let lab_done: boolean = false;
                    let lab_doing: boolean = false;
                    if (question.type === "lab") {
                        //console.log(`Answers:${JSON.stringify(props.answersData)}`);
                        const labAnswer = props.answersData?.answers?.[question.id];
                        if (labAnswer && typeof labAnswer === "object") {
                            if ("finished" in labAnswer)
                                lab_done = Boolean((labAnswer as { finished?: boolean }).finished);
                            if ("doing" in labAnswer)
                                lab_doing = Boolean((labAnswer as { doing?: boolean }).doing);
                        }
                    }
                    return (
                    <fieldset id={`fieldset_${question.id}`} className={styles.questionFieldset} key={question.id}>
                        <legend id={question.id} 
                                className={`${styles.questionLegend} ${lab_done ? styles.labDone : ""} ${lab_doing ? styles.labDoing : "" }`} 
                                title={`You scored ${pointsText(score)} on this question.`}>
                            <span className={`${styles.questionNumber} ${disabled ? styles.disabled : ""}`}>
                                {index+1}.&nbsp;
                            </span>
                            <span className={`${disabled ? styles.disabled : ""}`}>{question.text}</span>
                        </legend>
                        {question.text_additional && (
                            <div
                                className={`${styles.questionTextAdditional} ${disabled ? styles.disabled : ""}`}
                                dangerouslySetInnerHTML={{ __html: question.text_additional }}
                            />
                        )}
                        {(() => {
                            switch (question.type) {
                                case "closed":
                                case "multiple":
                                    let selectedAnswerValue : string|null = null;
                                    if (question.type === "closed") {
                                        selectedAnswerValue = getAnswerValue(question.type, answers[question.id], null);
                                        //console.log(`C:${question.id}:${selectedAnswerValue}`);
                                    }
                                    return question.answers.map((answer:IQuestionAnswer, index) => {
                                        //console.log(`Answer: ${JSON.stringify(answer)}`);
                                        const questionKey = Object.keys(answer)[0];
                                        const questionText = answer[questionKey];
                                        const isCorrect  = !!(result[`${questionKey}_is_correct`]??false);
                                        const correctValue = !!(result[`${questionKey}_correct_value`]??false);
                                        const score = Number(result[`${questionKey}_score`]) ?? 0;
                                        const reason = answer.reason;
                                        const type = question.type==="closed" ? "radio" : "checkbox";
                                        if (question.type === "multiple") {
                                            selectedAnswerValue = getAnswerValue(question.type, answers[question.id], questionKey);
                                            //console.log(`M:${question.id}/${questionKey}:${selectedAnswerValue}`);
                                        }
                                        const checked = selectedAnswerValue === questionKey;
                                        let answerClass = styles.answerChoice;
                                        if (submitted) {
                                            if (isCorrect)
                                                answerClass += ` ${styles.submitted} ${styles.submittedCorrect}`;
                                            else if (correctValue)
                                                answerClass += ` ${styles.submitted} ${styles.submittedWrongCorrect}`;
                                            else if (checked)
                                                answerClass += ` ${styles.submitted} ${styles.submittedIncorrect}`;
                                        }
                                        const inputClass = `${submitted ? styles.submitted : ""} ${correctValue ? styles.correct : styles.incorrect}`;
                                        return (
                                            <div key={questionKey}
                                                 className={answerClass}
                                                 title={`${(submitted && (checked || isCorrect || correctValue)) ? "You scored "+pointsText(score)+" here." : ""}`}
                                            >
                                                {/*<span>{checked?"T":"F"}/{isCorrect?"T":"F"}/{correctValue?"T":"F"}...</span>*/}
                                                <input
                                                    type={type}
                                                    id={`${question.id}/${questionKey}`}
                                                    name={question.id}
                                                    className={inputClass}
                                                    value={questionKey}
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={question.type==="closed" ? closedClicked : multipleClicked}
                                                />
                                                {question.type==="multiple" && (
                                                <span className={styles.checkmark}>
                                                    <img src="/static/img/tick.svg" alt="Tick indicator" />
                                                </span>
                                                )}
                                                <label
                                                    htmlFor={`${question.id}_${questionKey}`}
                                                    className={disabled ? styles.disabled : ""}
                                                >
                                                    {questionText}
                                                </label>
                                                {/*<p>SUBMITTED={submitted?"T":"F"} REASON={reason}</p>*/}
                                                {submitted && (
                                                    <p className={styles.reason}>{reason}</p>
                                                )}
                                            </div>
                                        )
                                    });
                                case "semaphore":
                                    return (
                                        <table className={`${styles.semaphoreTable} ${disabled ? styles.disabled : ""}`}>
                                            <thead>
                                                <tr>
                                                    <td>{submitted ? "" : "Choose one"}</td>
                                                    <td title="The answer is correct">Correct</td>
                                                    <td title="The answer is partially correct. Explain why!">Partially</td>
                                                    <td title="The answer is completely wrong">Wrong</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {question.answers.map((answer, index) => {
                                                const questionKey = Object.keys(answer)[0];
                                                const partialTextKey = `${question.id}/${questionKey}_partialtext`;
                                                const partialText = String(answers?.[partialTextKey] ?? "");
                                                const questionText = answer[questionKey];
                                                const isCorrect  = !!(result[`${questionKey}_is_correct`]??false);
                                                const correctValue = result[`${questionKey}_correct_value`];
                                                const score: number = Number(result[`${questionKey}_score`] ?? 0);
                                                const reason = answer.reason;
                                                const selectedAnswerValue = getAnswerValue(question.type, answers[question.id], questionKey);
                                                //console.log(`S:${question.id}/${questionKey}:${selectedAnswerValue}`); 
                                                let answerClass = styles.semaphoreRow;
                                                if (submitted)
                                                    answerClass += ` ${styles.submitted} ${isCorrect ? styles.submittedCorrect : styles.submittedIncorrect}`;
                                                return (
                                                    <tr key={questionKey} 
                                                        className={answerClass}
                                                        title={`${submitted ? "You scored "+pointsText(score)+" here." : ""}`}
                                                    >
                                                        <td className={`${styles.semaphoreText} ${submitted?styles.submitted:""} ${isCorrect ? styles.submittedCorrect : styles.submittedIncorrect}`}>
                                                            <label htmlFor={`${question.id}_${questionKey}`}
                                                                   className={disabled ? styles.disabled : ""}
                                                            >
                                                                {questionText}
                                                            </label>
                                                            {submitted && (
                                                                <p className={styles.reason}>{reason}</p>
                                                            )}
                                                            {props.assessmentObject.anonymous && selectedAnswerValue==="partial" && (
                                                                <div>
                                                                    {submitted ? (
                                                                        partialText && (
                                                                            <p className={styles.partialTextAnswer}>
                                                                                <span className={styles.label}>Your reasoning:&nbsp;</span>
                                                                                <span className={styles.answer}>{partialText}</span>
                                                                            </p>
                                                                        )
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            className={styles.partialTextInput}
                                                                            id={`${question.id}/${questionKey}_partialtext`} 
                                                                            name={`${question.id}_${questionKey}_partialtext`}
                                                                            value={String(answers[partialTextKey]??"")}
                                                                            readOnly={submitted}
                                                                            disabled={disabled}
                                                                            placeholder="Tell us why" 
                                                                            title="Tell us why you've chosen this to be partially true"
                                                                            onChange={(e) => {
                                                                                const value = e.currentTarget.value;
                                                                                setAnswers(prev => ({
                                                                                    ...prev,
                                                                                    [partialTextKey]: value
                                                                                }));
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        {
                                                        ["good", "partial", "wrong"].map((option) => {
                                                            const checked = option===selectedAnswerValue;
                                                            let inputClass = option;
                                                            if (submitted) {
                                                                inputClass += " " + styles.submitted + " " + (option===correctValue ? styles.correct : styles.incorrect);
                                                            }
                                                            return <td key={option}>
                                                                <input
                                                                    type="radio"
                                                                    className={inputClass}
                                                                    name={`${question.id}_${questionKey}`}
                                                                    id={`${question.id}/${questionKey}/${option}`}
                                                                    value={option}
                                                                    disabled={disabled}
                                                                    checked={checked}
                                                                    onChange={semaphoreClicked}
                                                                />
                                                            </td>
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    );
                                case "text":
                                        return (
                                            <div className={styles.answerText}>
                                                <textarea
                                                    id={question.id}
                                                    name={question.id}
                                                    rows={4}
                                                    cols={50}
                                                    disabled={disabled}
                                                />
                                            </div>
                                        );
                                case "lab":
                                    const lab_id = question.lab_id;
                                    //title={% if submitted and qscore>0 %}title="You scored {{pointstext(qscore)}} here."{% endif %}
                                    if (lab_id == null || submitted) // No button if there is no lab_id, or the assessment is already submitted
                                        return <></> 
                                    else {
                                        const vm = props.assessmentObject.vm ?? null;
                                        if (vm)
                                            continuouslyPollVM(vm);
                                        //console.log(`imageId=${vm.image_id} labid=${labid}`);
                                        return (
                                            <div className={`${styles.answerLab} ${lab_done ? styles.labFinished : ""}`}>
                                                <button
                                                    type="button"
                                                    className={`${styles.labButton}`}
                                                    onClick={ () => {
                                                        if (vm) {
                                                            startLab(
                                                                vm.image_id,
                                                                vm.region,
                                                                vm.cohort_id,
                                                                props.assessmentObject.lmodule_code,
                                                                lab_id,
                                                                (status_type, imageId, data) => {
                                                                    //console.log(`Lab status: ${status_type} imageId=${imageId} data=${JSON.stringify(data)}`);
                                                                    if (status_type==="init") {
                                                                        //console.log(`startLab init: ${JSON.stringify(data)}`);
                                                                        setProgressbarPercentage(0);
                                                                        setLabStatus("starting"); // It can't be anything else here, just "starting"
                                                                    }
                                                                    else if (status_type==="progress") {
                                                                        //console.log(`startLab progress: ${JSON.stringify(data)}`);
                                                                        setProgressbarPercentage(parseInt(data.proc??"0"));
                                                                        if (data.status) {
                                                                            var status = data.status ?? "";
                                                                            //Bit of cheating here
                                                                            if (status==="stopped") { // "stopped" state may occur at the beginning of the bootup process, so we change it to "starting"
                                                                                status = "starting";
                                                                            }
                                                                            else if (status==="no_instance") { // "no_instance" state may occur at the beginning of the creation process, so we change it to "creating"
                                                                                status = "creating";
                                                                            }
                                                                            setLabStatus(status);
                                                                        }
                                                                    }
                                                                    else if (status_type==="done") {
                                                                        //console.log(`startLab done: ${JSON.stringify(data)}`);
                                                                        if (data.status) {
                                                                            setLabStatus(data.status??"");
                                                                        }
                                                                        setProgressbarPercentage(null);
                                                                        continuouslyPollVM(vm);
                                                                    }
                                                                },
                                                                (user_message, log) => {
                                                                    console.error(`Lab error: ${user_message}`);
                                                                }
                                                            );
                                                        }
                                                    }}
                                                    disabled={disabled || !props.allowSubmit}
                                                >Start</button>
                                                {["A","D"].includes(isVMActive(labStatus)) ?
                                                    <div className={styles.start_lab_progressbar}>
                                                        <TinyProgressBar
                                                        percentage={progressbarPercentage??0}
                                                        status={getStatusText(labStatus)}
                                                        />
                                                    </div>
                                                    :
                                                    <p className={styles.start_lab_text}>{getStatusText(labStatus)}</p>
                                                }
                                            </div>
                                        );
                                    }
                                default:
                                    return <div>Unsupported question type ({question.type})</div>;
                            }
                        })()}
                    </fieldset>
                    )
                }
            })}
            </form>
                <div className={styles.submitButtonContainer}>
                {!submitted && active && (
                    <Loading isLoading={submitApiState.isLoading}>
                        <button
                            className={styles.submitButton}
                            onClick={() => saveAssessment(true)}
                            disabled={disabled || !props.allowSubmit}
                        >Submit</button>
                        {props.canSave && (
                        <button
                            className={styles.submitButton}
                            onClick={() => saveAssessment(false)}
                        >Save</button>
                        )}
                    </Loading>
                )}
                </div>
        </>
    )}
}
